/**
 * HeroPreviewPage — route /_preview
 * Renders the "עשיר" predefined template with live data and drag-to-reorder.
 * Embedded as an iframe inside HeroSection, CSS-scaled to fit the screen mockup.
 */
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dashboard from '../../containers/Dashboard/Dashboard';
import { dummyZmanimData } from '../../../dummies/WidgetPreview/dummyData';
import BoardOnboarding from '../../../business/BoardOnboarding/BoardOnboarding';

export const PREVIEW_W = 1600;
export const PREVIEW_H = 940;

const DEMO_ORG = {
  location: { lat: 31.7683, lng: 35.2137, timezone: 'Asia/Jerusalem' },
  address: { city: 'ירושלים' },
};

const HeroPreviewPage = () => {
  const dispatch = useDispatch();
  const predefinedTemplates = useSelector(state => state.app.predefinedTemplates);
  // Only show once parent page signals the screen is fully grown (via postMessage)
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingShown = useRef(false);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'INORAI_SCREEN_EXPANDED' && !onboardingShown.current) {
        onboardingShown.current = true;
        setShowOnboarding(true);
      } else if (event.data?.type === 'INORAI_SHOW_ONBOARDING') {
        setShowOnboarding(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Read the dashboard directly from Redux — all edits/drags update it naturally
  const reduxDashboard = useSelector(state => state.dashboard.dashboard);

  const firestoreTemplate = predefinedTemplates.find(t => t.name === 'עשיר');

  // Pre-seed zmanim with dummy data so Timeline shows times immediately (before API loads)
  const zmanimSeeded = useRef(false);
  useEffect(() => {
    if (zmanimSeeded.current) return;
    zmanimSeeded.current = true;
    dispatch({ type: 'FETCH_ZMANIM_SUCCESS', payload: dummyZmanimData.zmanim });
  }, [dispatch]);

  // Seed Redux only once the Firestore template is available — avoids flash of fallback template
  const seededWith = useRef(null);
  useEffect(() => {
    if (!firestoreTemplate) return;
    if (seededWith.current === 'firestore') return;
    seededWith.current = 'firestore';

    dispatch({
      type: 'INITIALIZE_DASHBOARD_SUCCESS',
      payload: {
        dashboard: {
          dashboardId: '_preview',
          current: {
            widgets: firestoreTemplate.widgets ?? [],
            settings: firestoreTemplate.settings ?? {},
          },
        },
      },
    });
  }, [firestoreTemplate, dispatch]);

  // While Redux hasn't been seeded yet, show nothing (brief flash)
  if (!reduxDashboard) return null;

  return (
    <div style={{ width: PREVIEW_W, height: PREVIEW_H, overflow: 'hidden', direction: 'ltr', position: 'relative' }}>
      <Dashboard
        dashboard={reduxDashboard}
        isEditMode={true}
        isDemo={true}
        dispatch={dispatch}
        organization={DEMO_ORG}
        hasUnsavedChanges={false}
        isSaving={false}
        onLogout={() => {}}
      />
      {showOnboarding && <BoardOnboarding isDemo onClose={() => setShowOnboarding(false)} />}
    </div>
  );
};

export default HeroPreviewPage;
